import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { t } from 'i18next'
import Product from '../Product'
import './productList.css'
import MiniLoading from '../MiniLoading'
import styles from './productList.module.css'
import MaterialsCalculator from 'Components/MaterialsCalculator/MaterialCalculator'
import Modal from 'react-bootstrap/Modal'
import { emptyCart } from 'redux/actions/cartActions'
import { useDispatch } from 'react-redux'
import { MATERIALS_CALCULATOR_CEILING_PARAM, ROUTES } from 'Utils/constants'
import { useHistory } from 'react-router-dom'
import { useVirtualizer } from '@tanstack/react-virtual'

const GRID_GAP = 10
const DEFAULT_CARD_WIDTH = 145
const DEFAULT_ROW_HEIGHT = 390

const getMinCardWidth = () => {
  if (typeof window === 'undefined') return DEFAULT_CARD_WIDTH
  if (window.innerWidth >= 2000) return 280
  if (window.innerWidth >= 1500) return 195
  return DEFAULT_CARD_WIDTH
}

const getEstimatedRowHeight = () => {
  if (typeof window === 'undefined') return DEFAULT_ROW_HEIGHT
  if (window.innerWidth >= 2000) return 560
  if (window.innerWidth >= 1500) return 430
  return DEFAULT_ROW_HEIGHT
}

const observeElementRectWithoutResizeObserver = (instance, callback) => {
  const element = instance.scrollElement
  if (!element) return undefined

  const targetWindow = element.ownerDocument?.defaultView ?? window
  const updateRect = () => {
    callback({
      width: element.clientWidth,
      height: element.clientHeight,
    })
  }

  updateRect()
  targetWindow.addEventListener('resize', updateRect, { passive: true })

  return () => {
    targetWindow.removeEventListener('resize', updateRect)
  }
}

function ProductList({
  products,
  setProductCart,
  successAlgoliaHits,
  productsRef,
  productsLoading,
  hasMore,
  handlePage,
  inListScreen,
  currentCategory,
  productSearchText,
  setShowMaterialsCalculatorPDP,
  setProductDetail,
}) {
  const [showMaterialsCalculatorPLP, setShowMaterialsCalculatorPLP] =
    useState(false)
  const [columnCount, setColumnCount] = useState(1)
  const [rowHeight, setRowHeight] = useState(getEstimatedRowHeight)
  const [scrollElement, setScrollElement] = useState(null)
  const containerRef = useRef(null)
  const loadMoreScrollTopRef = useRef(null)

  const dispatch = useDispatch()
  const history = useHistory()

  const showCalculatorAds =
    (currentCategory?.includes('Cielo rasos') && currentCategory?.length > 0) ||
    productSearchText.toLowerCase().includes('cielo raso') ||
    productSearchText.toLowerCase().includes('cielo rasos')

  const handleOpenCalculatorAd = useCallback(() => {
    history.push(
      `${ROUTES.materialsCalculator}?${MATERIALS_CALCULATOR_CEILING_PARAM}`
    )
    dispatch(emptyCart())
  }, [dispatch, history])

  const handleLoadMore = useCallback(() => {
    if (scrollElement) {
      loadMoreScrollTopRef.current = scrollElement.scrollTop
    }

    handlePage()
  }, [handlePage, scrollElement])

  const virtualItems = useMemo(() => {
    if (!productsLoading && showCalculatorAds) {
      return [
        { type: 'calculator-ad', id: 'materials-calculator-ad' },
        ...products,
      ]
    }
    return products
  }, [products, productsLoading, showCalculatorAds])

  const firstProductId =
    virtualItems[0]?.type === 'calculator-ad'
      ? virtualItems[1]?.ProductId ??
        virtualItems[1]?.ExternalCodes?.[0]?.ProductId
      : virtualItems[0]?.ProductId ??
        virtualItems[0]?.ExternalCodes?.[0]?.ProductId

  const rowCount = Math.ceil(virtualItems.length / columnCount)

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollElement,
    estimateSize: () => rowHeight,
    overscan: 3,
    observeElementRect: observeElementRectWithoutResizeObserver,
    useAnimationFrameWithResizeObserver: true,
    measureElement: element => element?.offsetHeight ?? rowHeight,
  })

  useLayoutEffect(() => {
    const parent = containerRef.current?.parentElement ?? null
    setScrollElement(parent)
    if (parent) parent.style.overflowAnchor = 'none'
    return () => {
      if (parent) parent.style.overflowAnchor = ''
    }
  }, [])

  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return undefined

    let rafId = null

    const updateColumnCount = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const nextColumnCount = Math.max(
          1,
          Math.floor(
            (element.clientWidth + GRID_GAP) / (getMinCardWidth() + GRID_GAP)
          )
        )
        const nextRowHeight = getEstimatedRowHeight()
        setColumnCount(prev =>
          prev === nextColumnCount ? prev : nextColumnCount
        )
        setRowHeight(prev => (prev === nextRowHeight ? prev : nextRowHeight))
      })
    }

    updateColumnCount()

    window.addEventListener('resize', updateColumnCount)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateColumnCount)
    }
  }, [])

  useEffect(() => {
    virtualizer.measure()
  }, [columnCount])

  useLayoutEffect(() => {
    if (!scrollElement || loadMoreScrollTopRef.current === null)
      return undefined

    const scrollTop = loadMoreScrollTopRef.current
    const rafId = requestAnimationFrame(() => {
      scrollElement.scrollTop = scrollTop

      if (!productsLoading) {
        loadMoreScrollTopRef.current = null
      }
    })

    return () => cancelAnimationFrame(rafId)
  }, [products.length, productsLoading, scrollElement])

  useEffect(() => {
    if (!scrollElement || !firstProductId) return

    scrollElement.scrollTop = 0
    virtualizer.scrollToOffset(0)
  }, [firstProductId, scrollElement, virtualizer])

  return (
    <>
      <div
        className="products-cotizacion-content"
        ref={containerRef}
        data-testid="container-product-list">
        <div
          className="product-cards-virtual-wrapper"
          data-testid="wrapper-product-cards">
          {virtualItems.length > 0 && (
            <div
              className="product-cards-virtual-stage"
              style={{ height: virtualizer.getTotalSize() }}>
              {virtualizer.getVirtualItems().map(virtualRow => {
                const startIndex = virtualRow.index * columnCount
                const rowItems = virtualItems.slice(
                  startIndex,
                  startIndex + columnCount
                )

                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="product-cards-virtual-row"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                    }}>
                    {rowItems.map(item =>
                      item.type === 'calculator-ad' ? (
                        <div
                          key={item.id}
                          className="product-card2 background-image"
                          data-testid="card-materials-calculator-ad"
                          onClick={handleOpenCalculatorAd}
                        />
                      ) : (
                        <div
                          key={
                            item.ProductId ?? item.ExternalCodes?.[0]?.ProductId
                          }>
                          <Product
                            product={item}
                            productsRef={productsRef}
                            inListScreen={inListScreen}
                            setShowMaterialsCalculatorPDP={
                              setShowMaterialsCalculatorPDP
                            }
                            setProductDetail={setProductDetail}
                          />
                        </div>
                      )
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {products.length === 0 && !successAlgoliaHits && (
            <div
              className={styles.noResultsContainer}
              data-testid="container-no-results">
              <h3>{t('PRODUCTS_SEARCH_T.RESULTS_NOT_FOUND')}</h3>
            </div>
          )}
        </div>
        {productsLoading ? (
          <div className="load-more-button" data-testid="loading-products">
            <MiniLoading productsLoading={productsLoading} />
          </div>
        ) : products.length > 0 && hasMore ? (
          <div className="load-more-button" data-testid="btn-load-more">
            <button
              type="button"
              onMouseDown={event => event.preventDefault()}
              onClick={handleLoadMore}
              disabled={productsLoading}>
              Ver más...
            </button>
          </div>
        ) : (
          <div className="load-more-button" data-testid="empty-load-more" />
        )}
      </div>
      <Modal
        backdrop={false}
        fullscreen={true}
        show={showMaterialsCalculatorPLP}
        data-testid="modal-materials-calculator">
        <MaterialsCalculator
          showCalculator={showMaterialsCalculatorPLP}
          setShowCalculator={setShowMaterialsCalculatorPLP}
          isPLP
        />
      </Modal>
    </>
  )
}

export default ProductList
